<template>
  <div :class="{ 'search-control_not-header': !useInHeader }" v-click-outside="onBlur" class="search-control">
    <div class="search-control__input-border"></div>
    <transition name="fade">
      <span v-show="showPlaceholder" class="search-control__placeholder">{{ placeholder }}</span>
    </transition>
    <span v-show="showHint" class="search-control__hint">{{ hint }}</span>
    <input
      @focus="onFocus"
      @keyup.enter="onEnterPress"
      @keyup.right="pasteHintToInput"
      @keyup.down="onClickArrowDown"
      @keyup.up="onClickArrowUp"
      @input="throttledSearch"
      v-model="searchText"
      type="text"
      ref="searchControlInput"
      class="search-control__input">
    <icon-base :white="useInHeader" :red="!useInHeader" class="search-control__icon-right">
      <icon-magnifier :id="`${id}__search-control-icon`"/>
    </icon-base>
    <div class="search-control__results" v-if="showResults">
      <div
        @click="onResultClick(row)"
        @mouseover="selectedIndex = index"
        class="search-control__result"
        v-for="(row, index) in rows" :key="row.id"
        :class="{'search-control__result_selected': checkItemIsSelected(index)}">
        <currency-icon :currencyId="row.id" class="search-control__icon"/>
        <span class="search-control__result-name">{{ row.name }} </span>
        <span class="search-control__result-symbol">({{row.symbol}})</span>
      </div>
      <div v-if="foundRowsCount > rows.length" class="search-control__result-info">There are {{ foundRowsCount - rows.length }} more currencies...</div>
      <div v-else-if="!foundRowsCount" class="search-control__result-info">Nothing found....</div>
    </div>
  </div>
</template>

<script>
/* @flow */
import throttle from 'lodash/throttle';
import { IconMagnifier } from 'Icons';

export default {
  components: {
    IconMagnifier,
  },
  props: {
    id: String,
    placeholder: String,
    useInHeader: Boolean,
    excludedTickers: {
      default: (): Array<number> => [],
      type: Array,
    },
  },
  data() { // eslint-disable-line
    return {
      searchText: '',
      rows: [],
      foundRowsCount: 0,
      requestInProgress: false,
      inputIsActive: false,
      selectedIndex: 0,
    };
  },
  computed: {
    showHint(): boolean {
      return this.searchText && this.hintIncludesSearchText && this.inputIsActive && this.searchStringRequiresHint; // eslint-disable-line max-len
    },
    searchStringRequiresHint(): boolean {
      return this.searchText.length <= 23;
    },
    hintIncludesSearchText(): boolean {
      const lowHint = this.hint.toLowerCase();
      const lowSearchText = this.searchText.toLowerCase();
      return lowHint.startsWith(lowSearchText);
    },
    hint(): string {
      return this.rows.length ? this.syncCapitalyze(this.searchText, this.rows[0].name) : '';
    },
    showPlaceholder(): boolean {
      return !this.inputIsActive && !this.searchText;
    },
    showResults(): boolean {
      return this.inputIsActive &&
        this.rows.length &&
        this.searchText &&
        !this.requestInProgress;
    },
  },
  mounted() {
    this.throttledSearch = throttle(this.search, 500);
  },
  methods: {
    checkItemIsSelected(index: number): boolean {
      return index === this.selectedIndex;
    },
    getNextIndex(arr: Object, index: number): number {
      const indexIsLast = (arr.length - 1) <= index;
      return indexIsLast ? 0 : index + 1;
    },
    getPrevIndex(arr: Object, index: number): number {
      const indexIsFirst = index <= 0;
      return indexIsFirst ? arr.length - 1 : index - 1;
    },
    onEnterPress() {
      if (this.showResults) {
        this.$emit('onEnterPress', this.rows[this.selectedIndex]);
        this.resetControl();
        this.$refs.searchControlInput.blur();
      }
    },
    onClickArrowDown() {
      if (this.rows.length) {
        this.selectedIndex = this.getNextIndex(this.rows, this.selectedIndex);
      }
    },
    onClickArrowUp() {
      if (this.rows.length) {
        this.selectedIndex = this.getPrevIndex(this.rows, this.selectedIndex);
      }
    },
    onResultClick(row: any) {
      this.$emit('onResultClick', row);
      this.resetControl();
    },
    resetControl() {
      this.searchText = '';
      this.clearResults();
      this.onBlur();
    },
    onBlur() {
      this.inputIsActive = false;
      this.selectedIndex = 0;
    },
    onFocus() {
      this.inputIsActive = true;
    },
    pasteHintToInput() {
      if (this.showHint) {
        this.searchText = this.hint;
      }
      this.throttledSearch();
    },
    replaceSubstrInString(str: string, index: number, substr: string): string {
      return str.substr(0, index) + substr + str.substr(index + substr.length);
    },

    /** Syncs chars capitalization from From to To and returns new To
     * Example: From = 'ZoO', To = 'zO';
     * Function will return 'Zo'
     */
    syncCapitalyze(from: string, to: string): string {
      let toCopy = to;
      for (let i = 0; i < from.length && i < toCopy.length; i += 1) {
        const fromIsCapital = this.charIsCapital(from[i]);
        const toIsCapital = this.charIsCapital(toCopy[i]);
        if (fromIsCapital && !toIsCapital) {
          toCopy = this.replaceSubstrInString(toCopy, i, toCopy[i].toUpperCase());
        } else if (!fromIsCapital && toIsCapital) {
          toCopy = this.replaceSubstrInString(toCopy, i, toCopy[i].toLowerCase());
        }
      }
      return toCopy;
    },
    charIsCapital(char: string): boolean {
      if (!Number.isNaN(+char)) {
        return false;
      }
      return char === char.toUpperCase();
    },
    search() {
      if (!this.searchText) {
        this.clearResults();
        return;
      }
      this.requestInProgress = true;
      this.selectedIndex = 0;
      this.$apiService.searchCurrency(this.searchText, this.excludedTickers, 5).then((data: any) => { // eslint-disable-line
        this.rows = data.rows;
        this.foundRowsCount = data.foundRowsCount;
        this.requestInProgress = false;
      });
    },
    clearResults() {
      this.rows = [];
      this.foundRowsCount = 0;
    },
    throttledSearch() {

    },
  },
  watch: {
    $route: {
      handler() {
        this.resetControl();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
  @mixin font() {
    font-family: Montserrat;
    font-style: normal;
    font-weight: 600;
    line-height: 21px;
    font-size: 13px;
  }

  @mixin placeholderPosition() {
    $lineHeight: 18px;
    top: 50%;
    margin-top: -$lineHeight/2;
    line-height: $lineHeight;
  }

  .search-control {
    $width: 299px;
    $input-height: 59px;
    $input-padding-left: 20px;
    $border-width: 1px;

    width: $width;
    height: $input-height;
    position: relative;
    &__input-border {
      display: none;
    }
    &__input {
      $border: $border-width solid $red-color2;

      border-left: $border;
      border-right: $border;
      border-top: none;
      border-bottom: none;
      width: $width;
      outline: none;
      height: $input-height;
      color: $red-color1;
      padding-left: $input-padding-left;
      padding-right: $input-padding-left*3;
      padding-top: 3px;
      padding-bottom: 3px;
      background: transparent;
      position: absolute;
      top: 0;
      left: 0;
      color: $white-color2;
      @include font;
      z-index: 1000;
    }
    &__results {
      width: $width;
      background: $white-color2;
      box-shadow: 2px 2px 30px $black-color2;
      border-radius: 0 0 5px 5px;
      z-index: 999;
      position: absolute;
      top: $input-height;
    }
    $result-padding: 0 15px;
    &__result {
      font-family: Montserrat;
      font-size: 13px;
      color: $black-color1;
      $height: 50px;
      width: 100%;
      height: $height;
      line-height: $height;
      padding: $result-padding;
      border-bottom: 1px solid $white-color1;
      cursor: pointer;
      display: flex;
      &_selected,
      &:hover {
        border-bottom-color: $red-color3;
        background: $red-color3;
        color: $white-color2;
      }
    }
    &__result-name {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    &__result-info {
      font-family: Montserrat;
      font-size: 13px;
      color: $grey-color9;
      $height: 40px;
      height: $height;
      line-height: $height;
      border-bottom: none;
      padding: $result-padding;
    }
    @mixin hint() {
        @include font;
        position: absolute;
        left: $input-padding-left + $border-width;
        @include placeholderPosition;
        letter-spacing: 0px;
        color: $red-color1;
    }
    &__hint {
      @include hint;
    }
    &__placeholder {
      @include hint;
      font-weight: 500;
    }
    &__icon {
      max-width: 20px;
      max-height: 20px;
      margin-right: 15px;
      margin-top: 5px;
    }
    &__icon-right {
      position: absolute;
      top: 23px;
      right: $input-padding-left+9;
    }
    &_not-header {
      $width2: 316px;
      $input-height2: 50px;
      $input-padding-left: 20px;
      &.search-control {
        width: 100%;
        max-width: $width2;
        height: $input-height2;
      }
      .search-control__input-border {
        background: $grey-color8;
        border-radius: 5px;
        width: 100%;
        max-width: $width2;
        height: $input-height2;
        display: inline-block;
        top: 0;
        left: 0;
        padding-left: $input-padding-left;
      }
      .search-control__icon-right {
        top: 19px;
        right: $input-padding-left;
      }
      .search-control__placeholder {
        @include placeholderPosition;
        color: $grey-color9;
        left: $input-padding-left;
        font-size: 14px;
        font-weight: normal;
      }
      .search-control__hint {
        left: $input-padding-left;
        font-weight: 500;
        font-size: 13px;
        @include placeholderPosition;
        color: $grey-color9;
      }
      .search-control__input {
        color: $black-color1;
        border: none;
        border-radius: 5px;
        width: 100%;
        max-width: $width2;
        height: $input-height2;
        padding-left: $input-padding-left;
        font-weight: 500;
        font-size: 13px;
      }
      .search-control__results {
        width: 100%;
        max-width: $width2;
        top: $input-height2 + 5px;
      }
    }
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity $transition;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/uikit/SearchControl.vue